import React, { useState } from 'react';


import { Stack } from 'ui/stack';

import Modal from 'components/partials/modal/modal';
import Button from 'components/partials/button/button';
import Input from 'components/partials/input/input';

type ModalActionType =
  | 'rename-group'
  | 'remove-group'
  | 'rename-regime'
  | 'duplicate-regime'
  | 'remove-regime';

interface ModalConfig {
  headerText: string;
  confirmLabel: string;
  hasInput?: boolean;
  inputLabel?: string;
  isRemove?: boolean;
  warning?: string;
}

const MODAL_CONFIG: Record<ModalActionType, ModalConfig> = {
  'rename-group': {
    headerText: 'Rename group of regimes',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Group name',
  },
  'remove-group': {
    headerText: 'Remove group of regimes',
    confirmLabel: 'Remove',
    isRemove: true,
    warning: `All regimes included into this group will be removed.\nAre you sure? This can't be undone`,
  },
  'rename-regime': {
    headerText: 'Rename regime',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Regime name',
  },
  'duplicate-regime': {
    headerText: 'Duplicate regime',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Regime name',
  },
  'remove-regime': {
    headerText: 'Remove regime',
    confirmLabel: 'Remove',
    isRemove: true,
    warning: `The regime will be removed. Are you sure?\nThis can't be undone`,
  },
};

interface ModalEntity {
  id?: string | number;
  name?: string;
  groupId?: string | number | null;
}

interface RegimeActionModalProps {
  type: string;
  entity?: ModalEntity | null;
  onClose: () => void;
  onConfirm: (arg?: string | number | null) => void;
  loading?: boolean;
}

const getInitialValue = (
  type: ModalActionType,
  entity?: ModalEntity | null
): string => {
  if (type === 'rename-group' || type === 'rename-regime') {
    return entity?.name ?? '';
  }

  if (type === 'duplicate-regime') {
    return entity?.name ? `${entity.name} copy` : '';
  }

  return '';
};

const RegimeActionModal = ({
  type,
  entity,
  onClose,
  onConfirm,
  loading = false,
}: RegimeActionModalProps): React.JSX.Element => {
  const config =
    MODAL_CONFIG[type as ModalActionType] ?? MODAL_CONFIG['rename-group'];

  const [inputValue, setInputValue] = useState(() =>
    getInitialValue(type as ModalActionType, entity)
  );

  const isConfirmDisabled = config.hasInput ? !inputValue.trim() : false;

  const handleConfirm = () => {
    if (config.hasInput) {
      onConfirm(inputValue.trim());
    } else {
      onConfirm();
    }
  };

  const footer = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
      <Button
        type="button"
        variant={config.isRemove ? 'critical-filled' : 'brand-filled'}
        size="md"
        disabled={isConfirmDisabled}
        loading={loading}
        onClick={handleConfirm}
        data-testid="confirm-action-button"
      >
        {config.confirmLabel}
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-action-button"
      >
        Cancel
      </Button>
    </Stack>
  );

  return (
    <Modal
      headerText={config.headerText}
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      footer={footer}
      skipUnmountOnClose
      primaryAction={{
        onClick: handleConfirm,
        disabled: isConfirmDisabled,
        loading,
      }}
      modalClassName="!w-[452px]"
      data-testid="regime-action-modal"
    >
      <div className="mt-4 space-y-4">
        {config.hasInput && (
          <Input
            label={config.inputLabel}
            labelInTop
            placeholder="Type..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            data-testid="action-modal-input"
          />
        )}

        {config.warning && (
          <p className="text-sm text-gray-600 whitespace-pre-line">
            {config.warning}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default RegimeActionModal;
